import { t } from '@hefe/i18n';
import {
  SelectOption,
  SelectOptionGroup,
  Task,
  TaskEvent,
  TaskEventType,
} from '@hefe/models';
import { TasksSlice } from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import { Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import InlineEditInput from '../../../../core/components/inline-edit-input/inline-edit-input.component';
import CustomTable from '../../../../core/components/table/custom-table.component';

interface TasksAutoAssignmentWizardPreviewComponentProps {
  task: Task;
  basketballTeamId?: number;
}

export default function TasksAutoAssignmentWizardPreviewComponent(
  props: TasksAutoAssignmentWizardPreviewComponentProps
) {
  const dispatch = useAppDispatch();
  const wizard = useSelector(TasksSlice.autoAssignmentWizardSelector);
  const assignablesSelectOptions = useSelector(
    TasksSlice.autoAssignmentWizardAssignablesSelectOptionsSelector
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (wizard.eventIds.length === 0 || wizard.assignableIds.length === 0) {
      return;
    }

    setLoading(true);
    dispatch(
      TasksSlice.fetchAutoAssignmentPreview({
        taskId: props.task.id,
        requestData: {
          assignable_type: wizard.mode,
          assignable_ids: wizard.assignableIds,
          event_ids: wizard.eventIds,
          number_of_assignments_per_event:
            wizard.options.numberOfAssigneesPerEvent,
          consider_prev_assignments: wizard.options.considerPrevAssignments,
          basketball_team_id: props.basketballTeamId,
        },
      })
    )
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [
    dispatch,
    props.basketballTeamId,
    props.task.id,
    wizard.assignableIds,
    wizard.eventIds,
    wizard.mode,
    wizard.options.considerPrevAssignments,
    wizard.options.numberOfAssigneesPerEvent,
  ]);

  const columns = useMemo(() => {
    return [
      {
        key: 'date',
        title: t('date'),
        dataIndex: 'date',
        default: true,
        render: (value: string) => formatDateTime(value),
      },
      {
        key: 'description',
        title: t('description'),
        dataIndex: 'description',
        default: true,
        render: (value: string, event: TaskEvent) =>
          event.type === TaskEventType.BASKETBALL_GAME ? value : t('practice'),
      },
      {
        key: 'location',
        title: t('location'),
        dataIndex: 'location',
        default: true,
      },
      {
        key: 'assignments',
        title: t('tasks.assignments'),
        dataIndex: 'assignments',
        default: true,
        render: (value: SelectOption[], event: TaskEvent) => {
          const names = value.map((assignment) => assignment.label).join(', ');
          return (
            <InlineEditInput
              highlightingMode="parent"
              component={Select}
              value={value.map((assignment) => assignment.value)}
              text={names}
              onOk={(value: number[]) =>
                dispatch(
                  TasksSlice.actions.setWizardPreviewAssignables({
                    eventId: event.id,
                    assignables: value,
                  })
                )
              }
              componentProps={{
                options: assignablesSelectOptions,
                mode: 'multiple',
                showSearch: true,
                optionFilterProp: 'children',
                filterOption: (input: string, option: SelectOptionGroup) => {
                  const isGroup = Array.isArray(option.options);
                  if (isGroup) {
                    return false;
                  }
                  return option.label
                    .toLowerCase()
                    .includes(input.toLowerCase());
                },
              }}
            />
          );
        },
      },
    ];
  }, [assignablesSelectOptions]);

  return (
    <CustomTable
      name="tasks-auto-assignment-wizard-preview-table"
      key="tasks-auto-assignment-wizard-preview-table"
      rowKey="id"
      hideColumnSelector
      columns={columns}
      dataSource={wizard.preview}
      loading={loading}
    />
  );
}
